import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, Send } from "lucide-react";
import { ApiError } from "@/api/client";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { RiaccediDialog } from "@/components/RiaccediDialog";
import { utenzeApi } from "@/api/utenze";
import { useAuth } from "@/auth/useAuth";
import type { UtenzaFornitoreDTO } from "@/types";

interface Props {
  oppId: string;
  utenza: UtenzaFornitoreDTO;
  // A invio concluso la pratica va riletta dal backend: cambiano stage e readiness.
  onInviata: () => void;
}

// Il backend rifiuta con un codice: qui diventa una frase che dice la causa e la via
// d'uscita (regole ux "error-clarity" / "error-recovery").
const MOTIVO_RIFIUTO: Record<string, string> = {
  utenza_da_ripristinare:
    "Utenza A2A da ripristinare: le pratiche restano in coda finché l'Affiliato non la ripristina.",
  invio_a2a_gia_in_corso: "Invio già in corso su questa utenza: attendi che finisca.",
  solo_sessione_temporanea: "Con le credenziali salvate l'invio è automatico: non serve farlo da qui.",
  utenza_non_assegnata: "Nessuna utenza A2A assegnata: chiedi all'amministratore",
  credenziali_da_inserire: "Credenziali da inserire: l'Affiliato le inserisce dal profilo",
  pratica_non_trovata: "Pratica non trovata.",
};

// Il 400 porta un detail oggetto ({codice, manca}): apiFetch riempie `detail` solo se è
// una stringa, quindi l'elenco si legge dal corpo grezzo rimasto in `message`.
function mancantiDa(e: ApiError): string[] | null {
  try {
    const detail = JSON.parse(e.message)?.detail;
    if (detail?.codice === "non_inviabile" && Array.isArray(detail.manca)) return detail.manca;
  } catch {
    // corpo non JSON: nessun elenco da mostrare
  }
  return null;
}

const attesa = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Invio definitivo ad A2A dalla pratica, per le agenzie che lavorano a sessione
 * temporanea (le utenze custodite partono da sole col cron). Conferma → job in
 * background → esito, sul modello di PannelloAzioniA2A.tsx lato admin.
 */
export function InvioA2ADaPratica({ oppId, utenza, onInviata }: Props) {
  const { hasRole } = useAuth();
  const [confermaAperta, setConfermaAperta] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inviata, setInviata] = useState(false);
  const [riaccediAperto, setRiaccediAperto] = useState(false);
  const vivo = useRef(true);
  useEffect(() => () => { vivo.current = false; }, []);

  function mostraErrore(e: unknown) {
    if (!(e instanceof ApiError)) {
      setErrore("Invio non riuscito. Riprova tra poco.");
      return;
    }
    if (e.detail === "sessione_fornitore_scaduta") {
      // L'Affiliato rientra da qui e l'invio riparte da solo; l'Operatore non ha
      // le credenziali del fornitore.
      if (hasRole("affiliato")) setRiaccediAperto(true);
      else setErrore("Sessione A2A scaduta: chiedi all'Affiliato di riaccedere.");
      return;
    }
    const manca = mancantiDa(e);
    if (manca) {
      setErrore(`Non inviabile: ${manca.length > 1 ? "mancano" : "manca"} ${manca.join(", ")}.`);
      return;
    }
    setErrore(MOTIVO_RIFIUTO[e.detail] ?? "Invio non riuscito. Riprova tra poco.");
  }

  // Giro di lettura limitato (~5 minuti): oltre, l'esito si rilegge ricaricando la
  // pagina — il job resta suo, il portale non lo insegue all'infinito.
  async function seguiJob(jobId: string) {
    for (let i = 0; i < 150; i++) {
      const st = await utenzeApi.inviaDaPraticaStatus(oppId, jobId);
      if (!vivo.current) return;
      if (st.stato === "completato") {
        setInviata(true);
        setProgress(null);
        onInviata();
        return;
      }
      if (st.stato === "errore") {
        setErrore(st.errore || "Invio non riuscito.");
        return;
      }
      setProgress(st.progress);
      await attesa(2000);
      if (!vivo.current) return;
    }
    setErrore("L'invio sta durando più del previsto: ricarica la pagina per vedere com'è finito.");
  }

  async function invia() {
    setErrore(null);
    setProgress(null);
    setInCorso(true);
    try {
      const { job_id } = await utenzeApi.inviaDaPratica(oppId);
      await seguiJob(job_id);
    } catch (e) {
      if (vivo.current) mostraErrore(e);
    } finally {
      if (vivo.current) setInCorso(false);
    }
  }

  return (
    <div className="bg-card p-4 rounded-lg border border-border space-y-3">
      <div>
        <h3 className="font-semibold text-text">Invio ad A2A</h3>
        <p className="text-xs text-muted-foreground">
          Utenza {utenza.username} · l'invio è definitivo e al cliente arriva l'OTP di accettazione.
        </p>
      </div>

      {!inviata && (
        <button
          type="button"
          disabled={inCorso}
          onClick={() => setConfermaAperta(true)}
          className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-45 disabled:cursor-not-allowed cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 inline-flex items-center justify-center gap-2"
        >
          {inCorso ? (
            <span
              aria-hidden="true"
              className="h-4 w-4 rounded-full border-2 border-primary-foreground border-t-transparent animate-spin"
            />
          ) : (
            <Send className="h-4 w-4" aria-hidden="true" />
          )}
          {inCorso ? "Invio in corso…" : "Invia ad A2A"}
        </button>
      )}

      <div aria-live="polite" className="space-y-1">
        {inCorso && progress && <p className="text-sm text-muted-foreground">{progress}</p>}
        {inviata && (
          <p className="flex items-center gap-2 text-sm text-green-700">
            <Check size={16} aria-hidden="true" className="shrink-0" />
            <span>Inviata ad A2A</span>
          </p>
        )}
      </div>

      {errore && (
        <p role="alert" className="flex items-start gap-2 text-sm text-red-600">
          <AlertTriangle size={16} aria-hidden="true" className="shrink-0 mt-0.5" />
          <span>{errore}</span>
        </p>
      )}

      <ConfirmDialog
        open={confermaAperta}
        title="Inviare la pratica ad A2A?"
        message={`La pratica verrà inviata definitivamente ad A2A con l'utenza ${utenza.username} e partirà l'OTP di accettazione al cliente. Confermi?`}
        onConfirm={() => { setConfermaAperta(false); invia(); }}
        onCancel={() => setConfermaAperta(false)}
      />

      <RiaccediDialog
        fornitore={utenza.fornitore}
        username={utenza.username}
        open={riaccediAperto}
        onClose={() => setRiaccediAperto(false)}
        onSuccess={() => { setRiaccediAperto(false); invia(); }}
      />
    </div>
  );
}
